import { useCallback } from 'react'

import { useFunkitBranding } from '../branding/BrandingContext'
import { useHapticFeedback } from '../haptics/HapticsContext'
import { type NestedSheet, useNestedSheet } from '../hooks/useNestedSheet'
import {
  type FeeDetailsRow,
  FeeDetailsScreen,
} from '../screens/FeeDetailsScreen'
import { Drawer } from './Drawer'

/** The drawer's own state, plus the fee line's press handler. */
export function useFeeDetailsSheet(): {
  sheet: NestedSheet<'feeDetails'>
  openFeeDetails: () => void
} {
  const haptic = useHapticFeedback()
  const sheet = useNestedSheet<'feeDetails'>()
  const { openNestedScreen } = sheet
  const openFeeDetails = useCallback(() => {
    // The drawer's own arrival is the feedback; the tap gets a light tick so it
    // lands in the same frame the sheet starts moving.
    void haptic()
    openNestedScreen('feeDetails')
  }, [haptic, openNestedScreen])
  return { sheet, openFeeDetails }
}

/**
 * The fee-disclosure drawer as the amount screens present it: a nested sheet
 * over the entry screen, opened from its fee line.
 */
export function FeeDetailsSheet({
  sheet,
  rows,
  summary,
}: {
  sheet: NestedSheet<'feeDetails'>
  rows: FeeDetailsRow[]
  /** Omitted when the rail's fee can't be worded (no method name, no rate). */
  summary?: string
}) {
  const { hideAttribution } = useFunkitBranding()

  return (
    <Drawer sheet={sheet}>
      {sheet.nestedScreen === 'feeDetails' ? (
        <FeeDetailsScreen
          {...(summary === undefined ? {} : { summary })}
          rows={rows}
          showAttribution={!hideAttribution}
        />
      ) : null}
    </Drawer>
  )
}
